import {
  Box,
  Container,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  Dialog,
  Fab,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  MyLocation,
  QrCodeScanner,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { toast } from 'react-toastify';
import {
  fetchRestaurants,
  setSearchTerm,
  setUserLocation,
  selectFilteredRestaurants,
} from './customerHomeSlice';
import { RootState } from '../../../app/store/configureStore';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { useTranslation } from 'react-i18next';

const CustomerHomePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const filteredRestaurants = useAppSelector(selectFilteredRestaurants);
  const searchTerm = useAppSelector(
    (state: RootState) => state.customerHome.searchTerm
  );
  const loading = useAppSelector(
    (state: RootState) => state.customerHome.loading
  );
  const [isScanning, setIsScanning] = useState(false);
  const [scanner, setScanner] = useState<Html5QrcodeScanner | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchRestaurants());
  }, [dispatch]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          dispatch(
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            })
          );
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error(t('customer.home.scan.camera-error'));
        }
      );
    }
  };

  const handleCloseScanner = () => {
    if (scanner) {
      scanner.clear();
    }
    setScanner(null);
    setIsScanning(false);
  };

  // QR Code Scanner
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());

      setIsScanning(true);

      function success(result: string) {
        handleCloseScanner();
        try {
          const url = new URL(result);
          const pathParts = url.pathname.split('/');
          const restaurantId = pathParts[pathParts.length - 1];
          const tableNumber = url.searchParams.get('table');

          if (restaurantId) {
            navigate(
              `/customer/restaurants/${restaurantId}${tableNumber ? `?table=${tableNumber}` : ''}`
            );
          }
        } catch {
          toast.error(t('customer.home.scan.invalid-qr'));
        }
      }

      function error(errorMessage: string) {
        console.warn(errorMessage);
      }

      setTimeout(() => {
        const newScanner = new Html5QrcodeScanner(
          'reader',
          {
            qrbox: { width: 250, height: 250 },
            fps: 5,
            rememberLastUsedCamera: true,
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          },
          false
        );

        setScanner(newScanner);
        newScanner.render(success, error);
      }, 100);
    } catch {
      handleCloseScanner();
      toast.error(t('customer.home.scan.camera-error'));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ p: 1 }}>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 2,
          py: 4,
          px: 2,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Typography variant="h3" gutterBottom>
          {t('customer.home.title')}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('customer.home.subtitle')}
        </Typography>

        {/* Search Bar */}
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('customer.home.search-placeholder')}
            value={searchTerm}
            onChange={(e) => dispatch(setSearchTerm(e.target.value))}
            sx={{
              bgcolor: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'grey.200',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'grey.300',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={getCurrentLocation} color="primary">
                    <MyLocation />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Restaurant List */}
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        ) : filteredRestaurants.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography color="text.secondary">
              {t('customer.home.no-restaurants')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: { xs: 1, sm: 2 } }}>
            {filteredRestaurants.map((restaurant) => (
              <Box
                key={restaurant.id}
                onClick={() =>
                  navigate(`/customer/restaurants/${restaurant.id}`)
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 2.5,
                  px: 3,
                  mb: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '& .arrow-icon': {
                      transform: 'translateX(4px)',
                      opacity: 1,
                    },
                  },
                  '&:active': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <Box
                  component="img"
                  src={restaurant.logoUrl}
                  alt={restaurant.name}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    objectFit: 'contain',
                    mr: 3,
                    border: '1px solid',
                    borderColor: 'grey.100',
                    p: 1,
                    bgcolor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'text.primary',
                      fontSize: '1.1rem',
                    }}
                  >
                    {restaurant.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {restaurant.distance
                      ? t('customer.home.distance.km-away', {
                          distance: restaurant.distance.toFixed(1),
                        })
                      : t('customer.home.distance.not-available')}
                  </Typography>
                </Box>
                <Box
                  className="arrow-icon"
                  sx={{
                    ml: 2,
                    color: 'primary.main',
                    transition: 'all 0.2s ease-in-out',
                    opacity: 0.5,
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                  }}
                >
                  <KeyboardArrowRight />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* QR Code Scanner FAB */}
      <Fab
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': {
            bgcolor: 'primary.main',
            filter: 'brightness(0.9)',
          },
        }}
        onClick={startScanner}
      >
        <QrCodeScanner />
      </Fab>

      {/* QR Scanner Dialog */}
      <Dialog
        open={isScanning}
        onClose={handleCloseScanner}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('customer.home.scan.title')}
          </Typography>
          <div
            id="reader"
            style={{
              width: '100%',
              minHeight: '300px',
            }}
          ></div>
          <Button
            variant="contained"
            onClick={handleCloseScanner}
            sx={{ mt: 2 }}
          >
            {t('common.cancel')}
          </Button>
        </Box>
      </Dialog>
    </Container>
  );
};

export default CustomerHomePage;
